import React, { useState, useRef, useEffect } from 'react';
import { apiService } from '../../../../services/api';
import { AnexoOrcamento } from '../../../../types/anexos_orcamento';
import { ProdutoOportunidade } from '../../../../types/orcamento';
import { Fornecedor } from '../../../../types/fornecedor';
import Select from 'react-select';

interface AnexoOrcamentoFormProps {
    opportunityId: number;
    anexo?: AnexoOrcamento | null;
    produtos: ProdutoOportunidade[];
    fornecedores: Fornecedor[];
    onSave: () => void;
    onCancel: () => void;
}

type TipoDocumento = 'Cotação Inicial' | 'Proposta Comercial' | 'Orçamento Final' | 'Nota Fiscal' | 'Outro';

// Estilo customizado para o Select que resolve o problema de z-index
const selectStyles = {
    menu: (provided: any) => ({
        ...provided,
        zIndex: 9999, // Este é o valor que garante que o menu dropdown fique acima de outros elementos
    })
};

const AnexoOrcamentoForm: React.FC<AnexoOrcamentoFormProps> = ({
    opportunityId,
    anexo,
    produtos,
    fornecedores,
    onSave,
    onCancel
}) => {
    // Estados do formulário
    const [titulo, setTitulo] = useState(anexo?.titulo || '');
    const [descricao, setDescricao] = useState(anexo?.descricao || '');
    const [tipoDocumento, setTipoDocumento] = useState<TipoDocumento>(anexo?.tipo_documento || 'Outro');
    const [idProduto, setIdProduto] = useState<number | null>(anexo?.id_produto_oportunidade || null);
    const [idFornecedor, setIdFornecedor] = useState<number | null>(anexo?.id_fornecedor || null);
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const fileInputRef = useRef<HTMLInputElement>(null);

    // Opções para o tipo de documento
    const tiposDocumento = [
        { value: 'Cotação Inicial', label: 'Cotação Inicial' },
        { value: 'Proposta Comercial', label: 'Proposta Comercial' },
        { value: 'Orçamento Final', label: 'Orçamento Final' },
        { value: 'Nota Fiscal', label: 'Nota Fiscal' },
        { value: 'Outro', label: 'Outro' }
    ];

    // Formatar tamanho do arquivo para exibição
    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Manipular upload de arquivo
    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files[0]) {
            setUploadedFile(files[0]);
        }
    };

    // Remover arquivo selecionado
    const handleRemoveFile = () => {
        setUploadedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Validar o formulário
    const validateForm = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        if (!titulo.trim()) {
            newErrors.titulo = 'O título é obrigatório';
        }

        if (!anexo && !uploadedFile) {
            newErrors.arquivo = 'É necessário anexar um arquivo';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Salvar o anexo
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        try {
            setIsSubmitting(true);

            // Preparar os dados - removendo campos undefined ou nulos
            const anexoData: any = {
                id_oportunidade: opportunityId,
                titulo,
                descricao,
                tipo_documento: tipoDocumento
            };

            // Adicionar produto apenas se for selecionado
            if (idProduto) {
                anexoData.id_produto_oportunidade = idProduto;
            }

            // Adicionar fornecedor apenas se for selecionado
            if (idFornecedor) {
                anexoData.id_fornecedor = idFornecedor;
            }

            if (anexo) {
                // Atualizar anexo existente
                if (uploadedFile) {
                    anexoData.arquivo = uploadedFile;
                }

                await apiService.updateAnexoOrcamento(anexo.id, anexoData);
            } else {
                // Criar novo anexo
                anexoData.arquivo = uploadedFile as File;

                await apiService.createAnexoOrcamento(anexoData);
            }

            onSave();
        } catch (error) {
            console.error('Erro ao salvar anexo:', error);
            alert('Erro ao salvar o anexo. Por favor, tente novamente.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <div className="row">
                <div className="col-md-6 mb-3">
                    <label className="form-label">
                        Título <span className="text-danger">*</span>
                    </label>
                    <input
                        className={`form-control ${errors.titulo ? 'is-invalid' : ''}`}
                        type="text"
                        value={titulo}
                        onChange={(e) => setTitulo(e.target.value)}
                        required
                        placeholder="Ex: Orçamento Fornecedor XYZ"
                    />
                    {errors.titulo && (
                        <div className="invalid-feedback">{errors.titulo}</div>
                    )}
                </div>

                <div className="col-md-6 mb-3">
                    <label className="form-label">
                        Tipo de Documento
                    </label>
                    <Select
                        id="tipo_documento"
                        name="tipo_documento"
                        placeholder="Selecione o tipo de documento"
                        options={tiposDocumento}
                        value={tiposDocumento.find(tipo => tipo.value === tipoDocumento) || null}
                        onChange={(option) => setTipoDocumento((option?.value as TipoDocumento) || 'Outro')}
                        className={errors.tipoDocumento ? 'is-invalid' : ''}
                        styles={selectStyles} // Aplicando os estilos aqui
                    />
                    {errors.tipoDocumento && (
                        <div className="invalid-feedback d-block">{errors.tipoDocumento}</div>
                    )}
                </div>
            </div>

            <div className="row">
                <div className="col-md-6 mb-3">
                    <label className="form-label">
                        Produto
                    </label>
                    <Select
                        id="id_produto_oportunidade"
                        name="id_produto_oportunidade"
                        placeholder="Selecione um produto (opcional)"
                        options={produtos.map(produto => ({
                            value: produto.id,
                            label: `${produto.nome_produto} - ${produto.quantidade} ${produto.unidade_medida}`
                        }))}
                        value={idProduto ? produtos
                            .filter(p => p.id === idProduto)
                            .map(p => ({ value: p.id, label: `${p.nome_produto} - ${p.quantidade} ${p.unidade_medida}` }))[0] : null}
                        onChange={(option) => setIdProduto(option?.value || null)}
                        isClearable
                        className={errors.idProduto ? 'is-invalid' : ''}
                        styles={selectStyles} // Aplicando os estilos aqui
                    />
                    {errors.idProduto && (
                        <div className="invalid-feedback d-block">{errors.idProduto}</div>
                    )}
                </div>

                <div className="col-md-6 mb-3">
                    <label className="form-label">
                        Fornecedor
                    </label>
                    <Select
                        id="id_fornecedor"
                        name="id_fornecedor"
                        placeholder="Selecione um fornecedor (opcional)"
                        options={fornecedores.map(fornecedor => ({
                            value: fornecedor.id,
                            label: fornecedor.nome
                        }))}
                        value={idFornecedor ? fornecedores
                            .filter(f => f.id === idFornecedor)
                            .map(f => ({ value: f.id, label: f.nome }))[0] : null}
                        onChange={(option) => setIdFornecedor(option?.value || null)}
                        isClearable
                        className={errors.idFornecedor ? 'is-invalid' : ''}
                        styles={selectStyles} // Aplicando os estilos aqui
                    />
                    {errors.idFornecedor && (
                        <div className="invalid-feedback d-block">{errors.idFornecedor}</div>
                    )}
                </div>
            </div>

            <div className="mb-3">
                <label className="form-label">
                    Descrição
                </label>
                <textarea
                    className="form-control"
                    rows={4}
                    value={descricao}
                    onChange={(e) => setDescricao(e.target.value)}
                    placeholder="Informações adicionais sobre o anexo (opcional)"
                />
            </div>

            <div className="mb-3">
                <label className="form-label">
                    Arquivo {!anexo && <span className="text-danger">*</span>}
                </label>
                <div className={`drag-attach ${errors.arquivo ? 'border-danger' : ''}`}>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        accept="*/*"
                    />
                    <div className="img-upload">
                        <i className="ti ti-file-upload" />
                        {anexo ? 'Substituir arquivo atual' : 'Enviar Arquivo'}
                    </div>
                </div>
                {errors.arquivo && (
                    <div className="text-danger small mt-1">{errors.arquivo}</div>
                )}
            </div>

            {uploadedFile && (
                <div className="mb-3">
                    <label className="form-label">Arquivo Selecionado</label>
                    <div className="upload-file upload-list">
                        <div>
                            <h6>{uploadedFile.name}</h6>
                            <p>{formatFileSize(uploadedFile.size)}</p>
                        </div>
                        <button
                            onClick={handleRemoveFile}
                            className="text-danger border-0 bg-transparent"
                            type="button"
                        >
                            <i className="ti ti-trash-x" />
                        </button>
                    </div>
                </div>
            )}

            {anexo && !uploadedFile && (
                <div className="mb-3">
                    <label className="form-label">Arquivo Atual</label>
                    <div className="upload-file upload-list bg-light-info">
                        <div>
                            <h6>{anexo.nome_arquivo}</h6>
                            <p>{formatFileSize(anexo.tamanho_arquivo)}</p>
                        </div>
                        <span className="badge bg-info">Atual</span>
                    </div>
                    <small className="text-muted">
                        Para substituir o arquivo atual, selecione um novo arquivo acima.
                    </small>
                </div>
            )}

            <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                    type="button"
                    className="btn btn-light"
                    onClick={onCancel}
                    disabled={isSubmitting}
                >
                    Cancelar
                </button>
                <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Salvando...
                        </>
                    ) : (
                        anexo ? 'Atualizar' : 'Confirmar'
                    )}
                </button>
            </div>
        </form>
    );
};

export default AnexoOrcamentoForm;